import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router, Params } from '@angular/router';
import {
  CellEditingStoppedEvent, CellValueChangedEvent, ColDef,
  ColGroupDef, DomLayoutType, GridApi,
  GridReadyEvent,
  ICellEditorParams, RowClassParams,
  ValueFormatterParams,
  CellDoubleClickedEvent,
  IAggFuncParams,
  INumberCellEditorParams
} from "ag-grid-community";
import * as math from 'mathjs';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Field, FieldDataSheet, FormulatedColumn, GeneralSelect, JSONPatchDocs, RunTicket } from '../../../_models/field';
import { FieldFilters } from '../../../_models/preferences';
import { UserProfile } from '../../../_models/user-profile';
import { PortalApiService } from '../../../_services/portal-api.service';
import { UserProfileService } from '../../../_services/user-profile.service';
import { FieldFilterService } from '../../_services/field-filter.service';

@Component({
  selector: 'app-view-datasheet',
  templateUrl: './view-datasheet.component.html',
  styleUrls: ['./view-datasheet.component.css'],
  providers: [DatePipe, DecimalPipe]
})
export class ViewDatasheetComponent implements OnInit, OnDestroy {
  @Input() isMonthlyView: boolean;
  @Input() defaultStartDate: string;
  @Input() receivedDataSheets: FieldDataSheet[];
  @Input() updateQueryParams: (paramName: string, paramValue: any) => void;
  jsonPatchDocsArr: JSONPatchDocs[] = [];
  formatStartDate: Date;
  formatEndDate: Date;
  isLoading: boolean = false;
  today: Date = new Date();
  entryDate: string;
  currentStartDate: string;
  currentEndDate: string;
  batteryOptions: GeneralSelect[] = [];
  tankOptions: GeneralSelect[] = [];
  displayColumns: GeneralSelect[] = [];
  formulatedColumns: FormulatedColumn[] = [];
  customDates: FormGroup;
  sumTotalBBLs: any;
  sumCalcBBLs: any;
  filters: FieldFilters = new FieldFilters;
  isLoadingField: boolean = false;
  field: Field;
  fieldOptions: Field[];
  userProfile: UserProfile;
  fieldID: number;
  datasheetID: number;
  initialLoad: boolean = true;
  showRequiredMsg: boolean = false;
  previousSelection: any = null;
  hasFilters: boolean = false;
  deleteDialogVisible: boolean = false;
  // Errors
  errorGetField: string;
  errorGetRunTickets: string;
  errorGetLayout: string;
  resultMessageClass: string;
  resultMessage: string;
  errorEditMessage: string;
  errorEditClass: string;
  errorGetTankVolume: boolean = false;

  // AG Grid properties
  themeClass = "ag-theme-quartz";
  receivedRunTickets: RunTicket[] = [];
  private gridApi!: GridApi<RunTicket>;
  public domLayout: DomLayoutType = "autoHeight";
  public colDefs: (ColDef | ColGroupDef)[];
  public defaultColDef: ColDef = {
    filter: true,
    wrapHeaderText: true,
    editable: true,
    width: 90,
    suppressMovable: true,
    valueFormatter: (params: ValueFormatterParams): any =>
      this.isEmptyPinnedCell(params)
        ? this.createPinnedCellPlaceholder(params)
        : undefined
  };
  paginationPageSize = 25;
  paginationPageSizeSelector = [25, 50, 100, 200];
  inputRow: {} = {};
  pinnedTopRowData = [];
  selectedRows: any[];
  selectedMode: string = 'currentPage';
  requiredFields: any[] = ['entryDate', 'batteryName', 'tankName', 'ticket', 'totalBBLs'];
  isPinnedRowReady: boolean = true;

  // Subjects
  private onDestroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private userService: UserProfileService,
    private apiService: PortalApiService,
    private fieldFilterService: FieldFilterService,
    private datePipe: DatePipe,
    private router: Router,
    private decimalPipe: DecimalPipe
  ) {
    this.userService.userProfile$.pipe(takeUntil(this.onDestroy$)).subscribe(
      userProfile => {
        this.userProfile = userProfile;
        //console.log(this.userProfile);
      });
  }

  ngOnInit() {
    this.route.params.subscribe(params => {
      this.fieldID = params['fieldID'];
      this.entryDate = params['entryDate'];
      this.datasheetID = params['datasheetID'];
    });

    // Check if the fieldID has changed, use the right fieldID
    this.route.queryParams.subscribe((params: Params) => {
      const newFieldID = params['newFieldID'];

      if (newFieldID) {
        this.getCompanyLayout(newFieldID);
        this.getFieldBatteries(newFieldID);
      } else {
        this.getCompanyLayout(this.fieldID);
        this.getFieldBatteries(this.fieldID);
      }
    });

    this.setDefaultDateRange();
    this.getFields();
    this.setDefaultDatesForFilter();
    this.onChanges();
  }

  onChanges(): void {
    let selectedFieldID = this.getCurrentFieldID();

    this.customDates = this.fb.group({
      startDate: [],
      endDate: []
    }, { validators: [this.dateRangeValidator] });

    this.customDates.valueChanges.subscribe(newValue => {
      this.filters = this.fieldFilterService.resetFilters();
      this.fieldFilterService.clearFilterParamsFromRoute();

      let customStartDate = newValue.startDate;
      let customEndDate = newValue.endDate;

      this.currentStartDate = this.datePipe.transform(customStartDate, 'yyyy-MM-dd');
      this.currentEndDate = this.datePipe.transform(customEndDate, 'yyyy-MM-dd');

      if (!this.initialLoad && this.dateRangeValidator &&
        this.currentStartDate != null && this.currentEndDate != null) {
        this.getFieldRunTickets(selectedFieldID);
      }
    });
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  setDefaultDateRange() {
    const defaultEndDate = new Date();

    this.currentStartDate = this.defaultStartDate;
    this.currentEndDate = this.datePipe.transform(defaultEndDate, 'yyyy-MM-dd');
  }

  setDefaultDatesForFilter() {
    const defaultStartDate: string = this.defaultStartDate + 'T00:00:00';
    const defaultEndDate: string = this.currentEndDate + 'T23:59:59';

    setTimeout(() => {
      this.formatStartDate = new Date(defaultStartDate);
      this.formatEndDate = new Date(defaultEndDate);
    });
  }

  // Check for End Date must be after Start Date in Custom filter
  dateRangeValidator(control: FormGroup) {
    const startDate = control.get('startDate')?.value;
    const endDate = control.get('endDate')?.value;

    if (endDate != null && endDate < startDate) {
      return { invalidDateRange: true };
    }

    return null;
  }

  sortDataByVisiblePosition(data) {
    data.sort((a, b) => {
      return a.ColumnHeader.VisiblePosition - b.ColumnHeader.VisiblePosition;
    });
  }

  formatCamelCaseToNormalText(camelCaseStr: string) {
    // Regular expression to separate camelCase into words
    const words = camelCaseStr.replace(/([a-z])([A-Z])/g, '$1 $2');
    // Capitalize first letter of each word
    return words.charAt(0).toUpperCase() + words.slice(1);
  }

  getFields() {
    this.isLoadingField = true;
    let selectedFieldID = this.getCurrentFieldID();

    if (this.userProfile.id) {
      this.apiService.getFields().subscribe({
        next: (data: Field[]) => {
          //console.log(data);
          this.fieldOptions = data;
          this.isLoadingField = false;

          data.map(field => {
            if (field.fieldID == selectedFieldID) {
              this.field = field;
            }
          })
        },
        error: err => {
          console.log(err);
          this.errorGetField = (err.error || 'An error occurred. Failed to load field.');
          this.isLoadingField = false;
        }
      });
    }
  }

  // Update column definitions based on batteryOptions
  updateColDefs() {
    const batteryNameList: string[] = this.batteryOptions.map(battery => battery.label);
    this.receivedRunTickets.map(ticket => ticket.entryDate = this.datePipe.transform(ticket.entryDate, 'yyy-MM-dd'));
    const today = this.datePipe.transform(new Date(), 'yyyy-MM-dd');

    this.colDefs = [
      {
        headerComponentParams: { template: '<i class="fas fa-trash"></i>' },
        headerCheckboxSelection: true,
        headerTooltip: 'Select all',
        checkboxSelection: params => params.node.level === -1 ? false : true,
        maxWidth: 70,
        sortable: false,
        headerCheckboxSelectionCurrentPageOnly: true,
        suppressHeaderMenuButton: true,
        editable: false
      },
      {
        headerName: 'Battery',
        headerTooltip: 'Battery',
        field: 'batteryName',
        tooltipField: 'batteryName',
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: { values: batteryNameList },
        minWidth: 175,
        maxWidth: 250,
        flex: 2
      },
      {
        headerName: 'Tank',
        headerTooltip: 'Tank',
        field: 'tankName',
        tooltipField: 'tankName',
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: this.tankCellEditorParams.bind(this),
        width: 120,
        valueFormatter: (params: ValueFormatterParams): any => {
          if (params.value) {
            return params.value;
          };

          if (params.node.level === -1) {
            return '';
          }
          return '*required';
        },
        cellClassRules: {
          'red-text': params => {
            return params.value === '*required' || params.value === null;
          }
        }
      }
    ];

    if (this.isMonthlyView) {
      const dateColumn: ColDef = {
        headerName: 'Date',
        headerTooltip: 'Date',
        field: 'entryDate',
        cellEditor: 'agDateCellEditor',
        cellEditorParams: {
          max: today
        },
        sort: 'desc',
        valueParser: (params) => {
          return new Date(params.newValue);
        },
        valueFormatter: (params) => {
          if (params.value) {
            return new Date(params.value).toLocaleDateString("en-us", {
              year: "numeric",
              month: "short",
              day: "numeric",
            });
          } else {
            if (params.node.level === -1) {
              if (this.receivedRunTickets.length === 1) {
                return `Total (1 entry)`;
              } else {
                return `Total (${this.receivedRunTickets.length} entries)`;
              }
            }
            return '*required';
          }
        },
        filter: "agSetColumnFilter",
        filterParams: {
          valueFormatter: (params: ValueFormatterParams) => {
            return params.value.split('T')[0];
          }
        },
        minWidth: 135
      }
      this.colDefs.splice(1, 0, dateColumn);
    }
  }

  getCurrentFieldID() {
    let selectedFieldID;

    this.route.queryParams.subscribe(params => {
      const paramValue = params['newFieldID'];

      if (paramValue) {
        selectedFieldID = paramValue;
      } else {
        selectedFieldID = this.fieldID;
      }
    });

    return selectedFieldID;
  }

  getFieldBatteries(fieldID: number) {
    this.apiService.getFieldBatteries(fieldID).subscribe({
      next: (data: any[]) => {
        this.batteryOptions = data.map(battery => ({
          label: battery.batteryName,
          value: battery.batteryID
        }));
        this.updateColDefs();
      },
      error: err => {
        console.log(err);
      }
    });
  }

  getFieldTanks(batteryID: number) {
    let selectedFieldID = this.getCurrentFieldID();

    return new Promise((resolve) => {
      this.apiService.getFieldTanks(selectedFieldID, batteryID).subscribe({
        next: (data: any[]) => {
          this.tankOptions = data.map(tank => ({
            label: tank.tankName,
            value: tank.tankID
          }));
          const tankNameList: string[] = this.tankOptions.map(tank => tank.label);
          resolve(tankNameList);
        },
        error: err => {
          console.log(err);
        }
      })
    })
  }

  getTankVolume(tankID: number, entryDate: string, height: number, params?) {
    return new Promise((resolve, reject) => {
      this.apiService.getTankVolFromHeight(tankID, entryDate, height).subscribe({
        next: (data: number) => {
          resolve(data);
          this.errorGetTankVolume = false;
        },
        error: err => {
          console.log(err);
          this.resultMessage = 'Invalid. Value exceeds tank height.';
          this.resultMessageClass = 'alert alert-danger';
          this.errorGetTankVolume = true;
          //reject(err);

          // reset tank volume to original value
          if (params) {
            const colId = params.column.getId();
            params.oldValue ?
              params.node.setDataValue(colId, params.oldValue) :
              params.node.setDataValue(colId, 0);
          }
        }
      })
    })
  }

  calcHeightToInches(ft: number, inches: number) {
    inches = Math.round(inches * 4) / 4;

    // If inches equals 12, convert it to 1 foot
    if (inches === 12) {
      ft = Number(ft) + 1; // Increment the feet by 1
      inches = 0; // Set inches to 0
    }

    return (ft * 12) + inches;
  }

  getCompanyLayout(fieldID: number) {
    this.isLoading = true;

    this.apiService.getCompanyLayout(fieldID).subscribe({
      next: (data: string) => {
        this.isLoading = false;
        const trimmedStr = data.substring(1, data.length - 1);
        const jsonStr = trimmedStr.replace(/\\/g, '');
        const jsonData = JSON.parse(jsonStr);

        this.sortDataByVisiblePosition(jsonData);

        jsonData.map(async column => {
          const columnKey = column.ColumnKey;
          const headerCaption = column.ColumnHeader.Caption;
          const fieldName = columnKey.charAt(0).toLowerCase() + columnKey.slice(1);

          if (column.Formula != "") {
            let formulatedColumn: FormulatedColumn = {
              caption: headerCaption,
              columnKey: columnKey,
              formula: this.processFormula(column.Formula)
            };
            this.formulatedColumns.push(formulatedColumn);
          }

          if (column.IsVisible) {
            switch (fieldName) {
              case 'tankName':
                break;

              case 'ticket':
                this.colDefs.push({
                  headerName: 'Ticket #',
                  headerTooltip: 'Ticket #',
                  field: 'ticket',
                  tooltipField: 'ticket',
                  minWidth: 100,
                  cellEditorParams: {
                    maxLength: 50
                  },
                  valueSetter: params => this.ticketNumValueSetter(params),
                  valueFormatter: (params: ValueFormatterParams): any =>
                    this.isEmptyPinnedCell(params)
                      ? this.createPinnedCellPlaceholder(params)
                      : undefined
                })
                break;

              // Read only Calc NSV BBLs column
              case 'correctedOilBBLs':
                this.colDefs.push({
                  headerName: headerCaption,
                  headerTooltip: headerCaption,
                  field: fieldName,
                  tooltipField: fieldName,
                  editable: false,
                  valueFormatter: (params) => {
                    if (params.value) {
                      return params.value.toLocaleString();
                    }
                  }
                })
                break;

              case 'topGaugeTemp':
                this.colDefs.push({
                  headerName: 'Gauge Temp',
                  marryChildren: true,
                  children: [
                    {
                      field: 'topGaugeTemp',
                      headerName: 'Top',
                      headerTooltip: 'Top Gauge Temp',
                      tooltipField: 'topGaugeTemp',
                      type: 'rightAligned',
                      headerClass: 'ag-left-aligned-header',
                      width: 70,
                      cellEditorParams: {
                        min: 0
                      } as INumberCellEditorParams,
                      valueSetter: params => this.valueSetter(params, 'topGaugeTemp'),
                      valueFormatter: (params) => {
                        return params.value
                          ? params.value.toLocaleString()
                          : ''
                      }
                    },
                    {
                      field: 'bottomGaugeTemp',
                      headerName: 'Bottom',
                      headerTooltip: 'Bottom Gauge Temp',
                      tooltipField: 'bottomGaugeTemp',
                      type: 'rightAligned',
                      headerClass: 'ag-left-aligned-header',
                      width: 80,
                      cellEditorParams: {
                        min: 0
                      } as INumberCellEditorParams,
                      valueSetter: params => this.valueSetter(params, 'bottomGaugeTemp'),
                      valueFormatter: (params) => {
                        return params.value
                          ? params.value.toLocaleString()
                          : ''
                      }
                    },
                  ]
                })
                break;

              case 'bottomGaugeTemp':
                break;

              case 'totalBBLs':
                this.colDefs.push({
                  headerName: "Total BBLs",
                  headerTooltip: "Total BBLs",
                  field: "totalBBLs",
                  tooltipField: "totalBBLs",
                  type: 'rightAligned',
                  headerClass: 'ag-left-aligned-header',
                  cellEditorParams: {
                    min: 0
                  } as INumberCellEditorParams,
                  aggFunc: customAggFunc,
                  suppressKeyboardEvent: params => {
                    if (params.node.rowPinned === "top") {
                      if (params.event.key === "Tab") {
                        this.isPinnedRowReady = false;
                      };
                      if (params.event.key === "Enter") {
                        this.isPinnedRowReady = true;
                      };
                    }
                    return false;
                  },
                  valueSetter: params => this.valueSetter(params, "totalBBLs"),
                  valueFormatter: (params: ValueFormatterParams): any => {
                    if (params.value) {
                      return params.value.toLocaleString();
                    };
                    return '*required';
                  }
                })
                break;

              case 'comments':
                this.colDefs.push({
                  headerName: "Comments",
                  headerTooltip: "Comments",
                  field: "comments",
                  tooltipField: "comments",
                  flex: 1,
                  minWidth: 100,
                  suppressKeyboardEvent: params => {
                    if (params.node.rowPinned === "top" && params.event.key === "Enter") {
                      this.isPinnedRowReady = true;
                    };

                    if (params.event.key === "Tab") {
                      return true;
                    };
                    return false;
                  }
                })
                break;

              default:
                this.colDefs.push({
                  headerName: headerCaption,
                  headerTooltip: headerCaption,
                  field: fieldName,
                  tooltipField: fieldName,
                  flex: 1,
                  minWidth: 100,
                  maxWidth: 150,
                  //TODO: Revisit for other layouts when a column does not have number type input
                  cellEditorParams: {
                    min: 0
                  } as INumberCellEditorParams,
                  type: 'rightAligned',
                  headerClass: 'ag-left-aligned-header',
                  valueSetter: params => this.valueSetter(params, fieldName),
                  valueFormatter: (params: ValueFormatterParams): any => {
                    if (params.value) {
                      return params.value.toLocaleString();
                    }
                    return this.isEmptyPinnedCell(params)
                      ? this.createPinnedCellPlaceholder(params)
                      : undefined
                  }
                })
            }
          }
        });
        // Insert Disposition Type column after Ticket column
        const ticketIndex = this.colDefs.findIndex((col: any) => col.field === "ticket");

        if (ticketIndex !== 1) {
          this.colDefs.splice(ticketIndex + 1, 0, {
            headerName: 'Disposition Type',
            headerTooltip: 'Disposition Type',
            field: 'dispositionTypeName',
            tooltipField: 'dispositionTypeName',
            editable: false,
            width: 90
          });
        }

        this.getFieldRunTickets(fieldID);
      },
      error: err => {
        console.log(err);
        this.isLoading = false;
        this.errorGetLayout = (err.error || 'An error occurred. Failed to get company layout.');
      }
    });
  }

  // Validations
  valueSetter(params, column) {
    const newValue = parseFloat(params.newValue);

    // Check if the value is negative
    if (isNaN(newValue) || newValue < 0) {
      this.errorEditMessage = 'Invalid. Value cannot be less than 0.';
      this.errorEditClass = 'alert alert-danger';
      return false; // Do not update the value if it’s negative
    }

    // Update the value if it's valid
    params.data[column] = params.newValue;
    return true;
  }

  topBottomValueSetter(params, column) {
    const newValue = parseFloat(params.newValue);
    const rowData = params.data;
    let topGauge;
    let bottomGauge;
    let topGaugeVolume;
    let bottomGaugeVolume;
    // Default to 0 if undefined
    const topFt = rowData.topFt ?? 0;
    const topIn = rowData.topIn ?? 0;
    const bottomFt = rowData.bottomFt ?? 0;
    const bottomIn = rowData.bottomIn ?? 0;

    if (column === 'topFt' || column === 'topIn') {
      if (column === 'topFt') {
        topGauge = this.calcHeightToInches(newValue, topIn);
        bottomGauge = this.calcHeightToInches(bottomFt, bottomIn);
      } else if (column === 'topIn') {
        topGauge = this.calcHeightToInches(topFt, newValue);
        bottomGauge = this.calcHeightToInches(bottomFt, bottomIn);
      }
    }

    if (column === 'bottomFt' || column === 'bottomIn') {
      if (column === 'bottomFt') {
        topGauge = this.calcHeightToInches(topFt, topIn);
        bottomGauge = this.calcHeightToInches(newValue, bottomIn);
      } else if (column === 'bottomIn') {
        topGauge = this.calcHeightToInches(topFt, topIn);
        bottomGauge = this.calcHeightToInches(bottomFt, newValue);
      }
    }

    if (column === 'bottomFt' || column === 'topFt') {
      if (isNaN(newValue) || newValue < 0 || topGauge < bottomGauge) {

        if (isNaN(newValue) || newValue < 0) {
          this.errorEditMessage = 'Invalid. Value cannot be less than 0.';
          this.errorEditClass = 'alert alert-danger';

        } else if (topGauge < bottomGauge) {
          this.errorEditMessage = 'Bottom cannot be greater than Top.';
          this.errorEditClass = 'alert alert-danger';
        }

        return false;
      }
    } else if (column === 'bottomIn' || column === 'topIn') {
      if (isNaN(newValue) || newValue < 0 || topGauge < bottomGauge || newValue >= 12) {
        // actually if newValue < 0 or >=12 it will become NaN
        if (isNaN(newValue) || newValue < 0 || newValue >= 12) {
          this.errorEditMessage = 'Invalid value: inches must be >= 0 and < 12. Inches will be rounded to the nearest 0.25" increment.';
          this.errorEditClass = 'alert alert-danger';

        } else if (topGauge < bottomGauge) {
          this.errorEditMessage = 'Bottom cannot be greater than Top.';
          this.errorEditClass = 'alert alert-danger';
        }

        return false;
      }

      // round down anything above 11.75 to 12
      if (this.isInRange(newValue)) {
        params.data[column] = 11.75;
      }
    }

    if ((column !== 'bottomIn' || column !== 'topIn') && !this.isInRange(newValue)) {
      params.data[column] = newValue;
    }
    return true;
  }

  isInRange(number: number): boolean {
    return number >= 11.75 && number < 12.00;
  }

  ticketNumValueSetter(params: any): boolean {
    const newValue = params.newValue;
    let trimmedValue;

    if (newValue === null) {
      this.errorEditMessage = 'Please enter a valid value.';
      this.errorEditClass = 'alert alert-danger';
      return false;
    } else {
      trimmedValue = newValue.trim();

      // Check if the trimmed value is different from the original value
      if (newValue !== trimmedValue) {
        this.errorEditMessage = 'Value cannot start or end with spaces.';
        this.errorEditClass = 'alert alert-danger';
        return false; // Reject the change
      }

      // Check if the trimmed value includes '%'
      /*if (trimmedValue.includes('%')) {
        this.errorEditMessage = 'Value cannot contain the "%" symbol.';
        this.errorEditClass = 'alert alert-danger';
        return false;
      }*/
    }

    params.data.ticket = trimmedValue; // Set the trimmed value
    return true;
  }

  addTankGaugesColumns() {
    const tankGaugesColumns = [
      {
        headerName: "Top",
        marryChildren: true,
        children: [
          {
            field: "topFt",
            headerName: "ft",
            headerTooltip: "Top ft",
            tooltipField: "topFt",
            type: 'rightAligned',
            headerClass: 'ag-left-aligned-header',
            minWidth: 60,
            cellEditorParams: {
              min: 0
            } as INumberCellEditorParams,
            valueSetter: params => this.topBottomValueSetter(params, "topFt"),
            valueFormatter: (params) => {
              return params.value || params.value == 0
                ? `${params.value.toLocaleString()} '`
                : ''
            },
            /*cellClassRules: {
              'rag-red-outer': params => {
                return this.errorGetTankVolume === true
              }
            }*/
          },
          {
            field: "topIn",
            headerName: "in",
            headerTooltip: 'Top in',
            tooltipField: "topIn",
            type: 'rightAligned',
            headerClass: 'ag-left-aligned-header',
            minWidth: 60,
            cellEditorParams: {
              min: 0,
              max: 11.99
            } as INumberCellEditorParams,
            valueSetter: params => this.topBottomValueSetter(params, "topIn"),
            valueFormatter: (params) => {
              return params.value || params.value == 0
                ? `${params.value.toLocaleString()} "`
                : ''
            }
          }
        ]
      },
      {
        headerName: "Bottom",
        marryChildren: true,
        children: [
          {
            field: "bottomFt",
            headerName: "ft",
            headerTooltip: 'Bottom ft',
            tooltipField: "bottomFt",
            type: 'rightAligned',
            headerClass: 'ag-left-aligned-header',
            minWidth: 60,
            cellEditorParams: {
              min: 0
            } as INumberCellEditorParams,
            valueSetter: params => this.topBottomValueSetter(params, "bottomFt"),
            valueFormatter: (params) => {
              return params.value || params.value == 0
                ? `${params.value.toLocaleString()} '`
                : ''
            }
          },
          {
            field: "bottomIn",
            headerName: "in",
            headerTooltip: 'Bottom in',
            tooltipField: "bottomIn",
            type: 'rightAligned',
            headerClass: 'ag-left-aligned-header',
            minWidth: 60,
            cellEditorParams: {
              min: 0,
              max: 11.99
            } as INumberCellEditorParams,
            valueSetter: params => this.topBottomValueSetter(params, "bottomIn"),
            valueFormatter: (params) => {
              return params.value || params.value == 0
                ? `${params.value.toLocaleString()} "`
                : ''
            }
          }
        ]
      }
    ]

    const containsTankGauges = tankGaugesColumns.some(tankGaugesObj =>
      tankGaugesObj.children.some(child =>
        this.colDefs.some((colDef: ColDef) =>
          child.field === colDef.field
        )
      )
    );

    if (!containsTankGauges) {
      const dispositionIndex = this.colDefs.findIndex((col: any) => col.field === "dispositionTypeName");
      if (dispositionIndex !== 1) {
        // Insert tankGaugesColumns after Disposition Type column
        this.colDefs.splice(dispositionIndex + 1, 0, ...tankGaugesColumns);

        // Add the new column after tankGaugesColumns
        this.colDefs.splice(dispositionIndex + 1 + tankGaugesColumns.length, 0, {
          headerName: "Calc BBLs",
          headerTooltip: "Calc BBLs",
          field: "calcBBLs",
          tooltipField: "calcBBLs",
          type: 'rightAligned',
          headerClass: 'ag-left-aligned-header',
          editable: false,
          width: 80,
          aggFunc: customAggFunc,
          valueFormatter: (params: ValueFormatterParams): any => {
            if (params.value && !isNaN(params.value)) {
              return params.value.toLocaleString();
            }
          }
        });
      }
    }
    this.getTankGaugesValues();
  }

  async calcFormulas(runTicket) {
    if (this.formulatedColumns) {
      for (const formulatedColumn of this.formulatedColumns) {
        let columnKey = formulatedColumn.columnKey;

        try {
          let result = await math.evaluate(formulatedColumn.formula, { runTicket: runTicket });
          //console.log(`Result for ${formulatedColumn.caption} ${formulatedColumn.formula} (columnKey: ${columnKey}): ${result}`);

          // convert columnKey to camelCase
          columnKey = columnKey.charAt(0).toLowerCase() + columnKey.slice(1);

          if (columnKey == 'correctedOilBBLs') {
            result = (Math.round(result * 100) / 100).toFixed(2); // convert to 2 decimal number
          }

          runTicket[columnKey] = result; // assign result to the right column
        } catch (error) {
          //console.error(`Error evaluating formula for ${formulatedColumn.caption} (columnKey: ${formulatedColumn.columnKey}):`, error);
        }
      }
    }
  }

  processFormula(formula: string) {
    // Remove 'ntt' occurrences
    formula = formula.replace(/ntt/g, '');

    // Remove 'n' occurrences
    formula = formula.replace(/ n /g, '');

    // Replace 'power' with 'pow'
    formula = formula.replace(/\bpower\b/g, 'pow');

    // Add 'runTicket' in front of any 'a60'
    formula = formula.replace(/\ba60\b/g, 'runTicket.a60');

    // Add 'runTicket' in front of any first capital letter that is after a square bracket
    formula = formula.replace(/\[([A-Z])(.*?)\]/g, (_, p1, p2) => 'runTicket.' + p1.toLowerCase() + p2);

    // Remove square brackets
    formula = formula.replace(/\[|\]/g, '');

    return formula;
  }

  calcGaugeToFtAndInches(gauge: number): { ft: number, inches: number } {
    const ft = Math.floor(gauge / 12);
    const inches = gauge - (ft * 12);
    return { ft, inches };
  }

  calcTankGaugesValues(runTicket: RunTicket) {
    runTicket.calcBBLs = runTicket.topGauge - runTicket.bottomGauge;

    runTicket.topFt = this.calcGaugeToFtAndInches(runTicket.topGauge).ft;
    runTicket.topIn = this.calcGaugeToFtAndInches(runTicket.topGauge).inches;
    runTicket.bottomFt = this.calcGaugeToFtAndInches(runTicket.bottomGauge).ft;
    runTicket.bottomIn = this.calcGaugeToFtAndInches(runTicket.bottomGauge).inches;
  }

  getTankGaugesValues(rowData?: RunTicket) {
    if (this.receivedRunTickets) {
      this.receivedRunTickets.forEach(runTicket => {
        this.calcTankGaugesValues(runTicket);
      });

      if (rowData) {
        this.calcTankGaugesValues(rowData);
      }
    }
  }

  getFieldRunTickets(fieldID: number) {
    this.resultMessage = null;
    this.errorEditMessage = null;
    this.isLoading = true;
    this.initialLoad = false;

    let startDate: string;
    let endDate: string;

    if (this.isMonthlyView) {
      startDate = this.currentStartDate;
      endDate = this.currentEndDate;
    } else {
      startDate = this.entryDate;
      endDate = this.entryDate;
    }

    if (this.userProfile.id) {
      this.apiService.getFieldRunTickets(fieldID, startDate, endDate).subscribe({
        next: (data: RunTicket[]) => {
          console.log(data);
          this.isLoading = false;

          if (data) {
            this.receivedRunTickets = data;

            data.map(runTicket => {
              this.calcFormulas(runTicket);
            });

            const hasTankGauges = this.colDefs.some(col => col.headerName === 'Top' || col.headerName === 'Bottom');
            if (!hasTankGauges) {
              this.addTankGaugesColumns();
            } else {
              this.getTankGaugesValues();
            }
          } else {
            this.receivedRunTickets = [];
            this.sumCalcBBLs = 0;
            this.sumTotalBBLs = 0;
          }
        },
        error: err => {
          console.log(err);
          this.errorGetRunTickets = (err.error || 'An error occurred. Failed to load run tickets.');
          this.isLoading = false;
        }
      });
    }
  }

  // Edit Run Ticket functionalities start here
  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.gridApi.addEventListener('filterChanged', () => this.checkFilters());
  }

  checkFilters() {
    const currentFilters = this.gridApi.getFilterModel();

    if (Object.keys(currentFilters).length > 0) {
      this.hasFilters = true;
    } else {
      this.hasFilters = false;
    }
  }

  clearFilters() {
    this.gridApi.setFilterModel(null);
    this.checkFilters();
  }

  async onCellValueChanged(params: CellValueChangedEvent) {
    //console.log(params);
    const colId = params.column.getId();

    if (params.oldValue !== params.newValue) {
      // If battery value has changed, set tank to null
      if (colId === 'batteryName') {
        const selectedBatteryName = params.newValue;
        const selectedTankName = params.data.tankName;

        // Find the selected battery using battery name to get batteryID
        const battery = this.batteryOptions.find(battery => battery.label === selectedBatteryName);
        await this.getFieldTanks(battery.value) || [];

        // If current tank doesn't belong to the selected battery, set it to null
        const currentTankIndex = this.tankOptions.findIndex(tank => tank.label === selectedTankName);
        if (currentTankIndex === -1) {
          params.node.setDataValue('tankName', null);

          // Add batteryID to inputRow
          if (params.rowPinned === 'top') {
            this.inputRow['batteryID'] = battery.value;
          }
        }
      } else {
        if (params.rowPinned !== 'top' && colId !== 'entryDate') {
          this.handleCellEdit(params);
        }
      }

      // Add data in the new run ticket
      if (params.rowPinned === 'top') {
        const tank = this.tankOptions.find(tank => tank.label === params.data.tankName);
        if (tank) {
          this.inputRow['tankID'] = tank.value;
        }
        this.inputRow[colId] = params.newValue;
      }
    }
  }

  tankCellEditorParams(params: ICellEditorParams<RunTicket>) {
    const selectedBatteryName = params.data.batteryName;

    // Find the selected battery using battery name to get batteryID
    const battery = this.batteryOptions.find(battery => battery.label === selectedBatteryName);
    const allowedTanks = this.getFieldTanks(battery.value);

    return {
      values: allowedTanks
    };
  }

  async findUpdatedValues(params: CellValueChangedEvent) {
    const oldValue = params.oldValue;
    const newValue = params.newValue;
    const path = params.colDef.field;
    const rowData = params.data;
    const updatedControls: { key: string, value: any }[] = [];

    if (oldValue !== newValue) {
      if (path !== 'entryDate') {
        const entryDate = this.datePipe.transform(rowData.entryDate, 'yyyy-MM-dd');

        switch (path) {
          // Recalculate topGauge if topFt or/and topIn change
          case ('topFt'):
          case ('topIn'):
            let topGauge = this.calcHeightToInches(rowData.topFt, rowData.topIn);
            let topGaugeVolume = await this.getTankVolume(rowData.tankID, entryDate, topGauge, params);

            // Push new topGauge value to updatedControls
            updatedControls.push(
              {
                key: 'topGauge',
                value: topGauge
              },
              {
                key: 'topGaugeVolume',
                value: topGaugeVolume
              });
            break;

          // Recalculate bottomGauge if bottomFt or/and bottomIn change
          case ('bottomFt'):
          case ('bottomIn'):
            let bottomGauge = this.calcHeightToInches(rowData.bottomFt, rowData.bottomIn);
            let bottomGaugeVolume = await this.getTankVolume(rowData.tankID, entryDate, bottomGauge);

            // Push new bottomGauge value to updatedControls
            updatedControls.push(
              {
                key: 'bottomGauge',
                value: bottomGauge
              },
              {
                key: 'bottomGaugeVolume',
                value: bottomGaugeVolume
              });
            break;

          case ('batteryName'):
            const battery = this.batteryOptions.find(battery => battery.label === rowData.batteryName);

            updatedControls.push({
              key: 'batteryID',
              value: battery.value
            })
            break;

          case ('tankName'):
            const tank = this.tankOptions.find(tank => tank.label === rowData.tankName);

            updatedControls.push({
              key: 'tankID',
              value: tank.value
            })
            break;

          default:
            updatedControls.push({
              key: path,
              value: newValue
            });
        }
      }
    }
    return updatedControls;
  }

  createJsonPatchDocs(updatedControls, ticketNum) {
    this.jsonPatchDocsArr = [];
    let jsonPatchDocs: JSONPatchDocs;

    updatedControls.forEach(updatedValue => {
      jsonPatchDocs = {
        op: "replace",
        path: `/${updatedValue.key}`,
        from: `${ticketNum}`,
        value: `${updatedValue.value}`
      };
      this.jsonPatchDocsArr.push(jsonPatchDocs);
    })
  }

  async handleCellEdit(params: CellValueChangedEvent) {
    const oldValue = params.oldValue;
    const path = params.colDef.field;
    const rowData = params.data;
    let ticketNumber: any;
    const formattedEntryDate = this.datePipe.transform(rowData.entryDate, 'yyyy-MM-dd');

    if (path === 'ticket') {
      ticketNumber = oldValue;
    } else {
      ticketNumber = rowData.ticket;
    }

    // check if there are new changes
    const updatedControls = await this.findUpdatedValues(params);

    // if so, create json patch docs
    if (updatedControls.length > 0) {
      this.createJsonPatchDocs(updatedControls, ticketNumber);
    }

    if (this.jsonPatchDocsArr.length > 0) {
      this.apiService.updateRunTicket(rowData.tankID, formattedEntryDate, this.jsonPatchDocsArr).subscribe({
        next: () => {
          this.updateDataOnGrid(updatedControls, rowData);
          console.log('Updated.');
          /*this.resultMessage = 'Updated.';
          this.resultMessageClass = 'alert alert-success';*/
        },
        error: err => {
          console.log(err);
          this.resultMessage = 'An error occurred. Failed to update run ticket.';
          this.resultMessageClass = 'alert alert-danger';
        }
      });
    }
  }

  onFieldChange(event) {
    this.pinnedTopRowData = [];
    this.showRequiredMsg = false;
    const selectedField: Field = event.value;
    this.updateQueryParams('newFieldID', selectedField.fieldID);
  }

  async updateDataOnGrid(updatedControls, rowData) {
    updatedControls.forEach(control => {
      // Check if the key exists in the new value object
      if (control.key in rowData) {
        // Update the rowData object with the new value
        rowData[control.key] = control.value;
      }
    })

    await this.calcFormulas(rowData); // calculate the formulas with new values
    this.getTankGaugesValues(rowData);
    this.gridApi.refreshCells();
  }

  getRowStyle({ node }: RowClassParams) {
    if (node.rowPinned) {
      return { fontStyle: 'italic', color: 'red' };
    }

    if (node.level === -1) {
      return { backgroundColor: '#68baf2', fontWeight: 'bold' };
    }
  }

  isDatasheetAvailable(selectedDate: Date): boolean {
    const selectedDateStr = this.datePipe.transform(selectedDate, 'yyyy-MM-dd');

    let dateStringDatasheets: string[] = [];

    this.receivedDataSheets.map(datasheet => {
      const dsEntryDate = this.datePipe.transform(datasheet.entryDate, 'yyyy-MM-dd');
      dateStringDatasheets.push(dsEntryDate);
    });

    return (dateStringDatasheets.includes(selectedDateStr));
  }

  handleDateChange(params) {
    const originalDate = this.datePipe.transform(params.oldValue, 'yyyy-MM-dd');
    const selectedDate = this.datePipe.transform(params.newValue, 'yyyy-MM-dd');
    const rowData = params.data;

    if (originalDate !== selectedDate) {
      this.apiService.updateRunTicketDate(rowData.tankID, originalDate, selectedDate, rowData.ticket).subscribe({
        next: (data) => {
          this.entryDate = selectedDate;
          console.log('Date updated.');
          /*this.resultMessage = 'Updated.';
          this.resultMessageClass = 'alert alert-success';*/
        },
        error: err => {
          console.log(err);
          this.resultMessage = (err.error || 'An error occurred. Failed to update run ticket entry date.');
          this.resultMessageClass = 'alert alert-danger';
        }
      });
    } else {
      console.log('Date didnt change.');
    }
  }

  async onCellEditingStopped(params: CellEditingStoppedEvent) {
    //console.log(params);
    this.resultMessage = null;
    const rowData = params.data;
    const colId = params.column.getId();

    // Show error message for cell that didn't have a value, because its valueSetter didn't get triggered
    if ((params.oldValue === null || params.oldValue === undefined)
      && !params.valueChanged) {
      this.errorEditMessage = 'Please enter a valid value.';
      this.errorEditClass = 'alert alert-danger';
    }

    // For adding a run ticket
    if (params.rowPinned === 'top') {
      if (!rowData.tankID && !rowData.entryDate &&
        (colId === 'topFt' || colId === 'topIn' || colId === 'bottomFt' || colId === 'bottomIn')) {
        params.node.setDataValue(colId, 0);
        this.resultMessage = 'Please select a tank and date first.';
        this.resultMessageClass = 'alert alert-danger';
      } else {
        this.inputRow['entryDate'] = this.datePipe.transform(rowData.entryDate, 'yyyy-MM-dd') + 'T00:00:00';

        // Calculate top and bottom tank
        const topFt = rowData.topFt ?? 0; // Default to 0 if undefined
        const topIn = rowData.topIn ?? 0;
        const bottomFt = rowData.bottomFt ?? 0;
        const bottomIn = rowData.bottomIn ?? 0;

        if (topFt > 0 || topIn > 0) {
          this.inputRow['topGauge'] = this.calcHeightToInches(topFt, topIn);
          this.inputRow['topGaugeVolume'] = await this.getTankVolume(rowData.tankID, rowData.entryDate, this.inputRow['topGauge'], params);
        } else {
          this.inputRow['topGauge'] = 0;
          this.inputRow['topGaugeVolume'] = 0;
        }

        if (bottomFt > 0 || bottomIn > 0) {
          this.inputRow['bottomGauge'] = this.calcHeightToInches(bottomFt, bottomIn);
          this.inputRow['bottomGaugeVolume'] = await this.getTankVolume(rowData.tankID, rowData.entryDate, this.inputRow['bottomGauge'], params);
        } else {
          this.inputRow['bottomGauge'] = 0;
          this.inputRow['bottomGaugeVolume'] = 0;
        }
      }

      if (colId === 'entryDate') {
        // check if data sheet is not available
        if (rowData.entryDate && !this.isDatasheetAvailable(rowData.entryDate)) {
          params.node.setDataValue('entryDate', undefined);
          console.error('Data sheet is not available');
          this.resultMessage = 'Data sheet for this date is not available. Please select a different date or contact support to create a new data sheet.';
          this.resultMessageClass = 'alert alert-danger';
        }
      }

      //console.log(this.isPinnedRowReady);
      if (this.isPinnedRowDataCompleted(params) && this.isPinnedRowReady) {
        let newRunTicket: RunTicket = { ...this.inputRow } as RunTicket;

        this.apiService.addRunTicket(rowData.tankID, this.inputRow['entryDate'], newRunTicket)
          .subscribe({
            next: data => {
              this.calcTankGaugesValues(newRunTicket);
              this.calcFormulas(newRunTicket);
              newRunTicket.dispositionTypeName = 'Sale';
              this.receivedRunTickets = [...this.receivedRunTickets, newRunTicket];
              setTimeout(() => {
                this.resultMessage = "Successfully created run ticket.";
                this.resultMessageClass = 'alert alert-success';
              }, 1000);
            },
            error: err => {
              console.log(err);
              this.resultMessage = (err.error || 'An error occurred. Please check for invalid inputs and try again.');
              this.resultMessageClass = 'alert alert-danger';
            }
          });

        // reset pinned row
        this.inputRow = {};
        this.pinnedTopRowData = [this.inputRow];
      }

    } else {
      // if rowPinned !== 'top' and colId === 'entryDate' -> handleDateChange();
      this.handleDatePicker(params);
    }
  }

  private handleDatePicker(params) {
    const colId = params.column.getId();

    if (colId === 'entryDate') {
      if (params.newValue) {
        if (this.isDatasheetAvailable(params.newValue)) {
          this.handleDateChange(params);
        } else {
          // Set date back to original
          params.node.setDataValue('entryDate', params.oldValue);
          console.error('Data sheet is not available');
          this.resultMessage = 'Data sheet for this date is not available. Please select a different date or contact support to create a new data sheet.';
          this.resultMessageClass = 'alert alert-danger';
        }
      } else {
        params.node.setDataValue('entryDate', params.oldValue);
      }
    }
  }

  private isPinnedRowDataCompleted({ rowPinned }): boolean {
    //TODO: Add any formulated columns to this array
    const fieldsToRemove = ['correctedOilBBLs', 'calcBBLs'];

    // Create a copy of colDefs excluding the specified fields/using headerName
    const colDefsCopy = this.colDefs.filter((colDef: ColDef) => !fieldsToRemove.includes(colDef.field));

    // remove the checkbox column at index 0
    colDefsCopy.splice(0, 1);

    if (rowPinned !== 'top') return;

    const allCompleted = colDefsCopy.every((colDef: ColDef) => {
      // Optional columns
      if (!this.requiredFields.includes(colDef.field)) {
        return true;
      }

      // Check if the colDef has children
      if (colDef['children']) {
        const allChildrenCompleted = colDef['children'].every(child => {
          const value = this.inputRow[child.field];
          return !!value; // Check if the value exists
        });
        return allChildrenCompleted;
      }

      // Check the main field
      const value = this.inputRow[colDef.field];
      return !!value;
    });

    return allCompleted;
  }

  private isEmptyPinnedCell({ node, value }: ValueFormatterParams): boolean {
    return (
      (node.rowPinned === 'top' && value == null) ||
      (node.rowPinned === 'top' && value === '')
    );
  }

  private createPinnedCellPlaceholder({ colDef }: ValueFormatterParams): string {
    // set checkbox column to not editable
    if (colDef.checkboxSelection) {
      colDef.editable = false;
    }

    if (!colDef.editable) {
      return null;
    }


    if (this.requiredFields.includes(colDef.field)) {
      return '*required';
    }
    return '';
  }

  onAddBtn() {
    this.resultMessage = null;
    this.errorEditMessage = null;
    this.inputRow = {};

    if (this.pinnedTopRowData.length === 0) {
      this.pinnedTopRowData = [this.inputRow];
      this.showRequiredMsg = true;
    } else {
      this.pinnedTopRowData = [];
      this.showRequiredMsg = false;
    }
  }

  onDeleteBtn() {
    this.resultMessage = null;
    this.errorEditMessage = null;
    this.deleteDialogVisible = true;
  }

  onClearSelectionBtn() {
    this.resultMessage = null;
    this.errorEditMessage = null;
    this.gridApi.deselectAll();
  }

  // Confirm Detele
  onConfirmSelected() {
    this.resultMessage = null;
    this.errorEditMessage = null;
    this.deleteDialogVisible = false;

    this.selectedRows.forEach(selectedRow => {
      const selectedDateStr = this.datePipe.transform(selectedRow.entryDate, 'yyyy-MM-dd');

      this.apiService.removeRunTicket(selectedRow.tankID, selectedDateStr, selectedRow.ticket).subscribe({
        next: () => {
          console.log(`Removed ticket#: ${selectedRow.ticket}`);
        },
        error: err => {
          console.log(err);
          this.deleteDialogVisible = false;
          this.resultMessage = 'An error occurred. Failed to delete run ticket. Please refresh the page and try again.';
          this.resultMessageClass = 'alert alert-danger';
        }
      })
    });
    this.gridApi.applyTransactionAsync({ remove: this.selectedRows });
  }

  async onSelectionChanged(event: any) {
    this.resultMessage = null;
    this.errorEditMessage = null;

    this.selectedRows = event.api.getSelectedRows();
    console.log('Selected Rows:', this.selectedRows);

    if (this.selectedRows.length > 0) {
      // Set single or plural ticket(s) in result message 
      this.selectedRows.length === 1 ?
        this.resultMessage = "1 run ticket selected" :
        this.resultMessage = `${this.selectedRows.length} run tickets selected`;

      this.resultMessageClass = 'alert alert-secondary';
    }

    // Check if there was a previous selection
    if (this.previousSelection && this.previousSelection.tankName === null) {
      this.resultMessage = 'A new tank must be selected after changing battery.';
      this.resultMessageClass = 'alert alert-danger';

      // Find previous battery and tank using IDs
      const battery = this.batteryOptions.find(battery => battery.value === this.previousSelection.batteryID);
      await this.getFieldTanks(battery.value);
      const tank = this.tankOptions.find(tank => tank.value === this.previousSelection.tankID);

      // Revert back to old values
      this.previousSelection.batteryName = battery.label;
      this.previousSelection.tankName = tank.label;
      this.gridApi.refreshCells();
    }

    if (this.selectedRows.length > 0) {
      this.previousSelection = this.selectedRows[this.selectedRows.length - 1]; // Get the last row selected
    } else {
      this.previousSelection = null; // Reset if no rows are selected
    }
  }

  onCellDoubleClicked(event: CellDoubleClickedEvent) {
    this.resultMessage = null;
    this.errorEditMessage = null;

    if (!event.data.batteryName &&
      event.colDef.field === 'tankName' &&
      event.rowPinned === 'top') {
      this.resultMessage = 'Please select a battery first.';
      this.resultMessageClass = 'alert alert-danger';
    }

    if (event.colDef.field === 'batteryName' && this.batteryOptions.length === 0) {
      this.resultMessage = 'Oops! No battery found in this field. Please contact support for more details.';
      this.resultMessageClass = 'alert alert-warning';
    }
  }
}

function customAggFunc(params: IAggFuncParams) {
  let sum = 0;

  // Sum function
  params.values.forEach((value) => {
    if (typeof value === "number") {
      sum += value;
    }
  });

  // Display max 2 decimal numbers
  let shortenDec = sum.toFixed(2);
  // Add comma after the third element from the right
  const result = shortenDec.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  // sometimes this displays 3 decimals
  //const result = sum.toLocaleString();
  return result;
}
